import { useState, useEffect } from 'react';
import { InvestorCustomTagCategory } from 'further-types/investor';
import { InvestorCustomTagCategoryLabels } from 'further-ui/labels';
import RecordRow from 'components/RecordRow';
import CustomTagSection from 'components/CustomTagSelector';
import api from 'lib/trpcClient';
import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Box, styled } from '@mui/material';

const tagCategoryOptions = Object.values(InvestorCustomTagCategory).map(
  (category) => ({
    label: InvestorCustomTagCategoryLabels[category],
    value: category,
  }),
);

type Props = {
  firmId?: string;
  investorId?: string;
  customTags?: Array<{
    category: InvestorCustomTagCategory;
    tag: string;
  }>;
  canEdit: boolean;
};

const StyledBox = styled(Box)({
  width: '100%',
});

const InvestorDetailsCustomTags: React.FC<Props> = ({
  firmId,
  investorId,
  customTags,
  canEdit,
}) => {
  const [customTagCategory, setCustomTagCategory] = useState<string>();
  const [editableCustomTags, setEditableCustomTags] = useState(
    customTags ?? [],
  );

  useEffect(() => {
    setEditableCustomTags(customTags ?? []);
  }, [customTags]);

  const { data: tagData } = api.investor.listCustomTags.useQuery(
    { category: customTagCategory, firmId: firmId ?? '' },
    { enabled: Boolean(customTagCategory && firmId) },
  );

  const invalidations = investorId
    ? [
        getQueryKey(api.investor.getInvestor, { investorId }, 'query'),
        getQueryKey(api.investor.listInvestors, undefined, 'query'),
      ]
    : undefined;

  const { mutate: setCustomTags } = api.investor.setCustomTags.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Custom tags have been updated.',
      invalidations,
    }),
  );

  const tagOptions = (tagData ?? []).map((tag) => tag.tag);

  const handleCustomTagsChange = (
    tags: Array<{ category: string; tag: string }>,
  ) => {
    const mappedTags = tags.map((tag) => ({
      category: tag.category as InvestorCustomTagCategory,
      tag: tag.tag,
    }));

    setEditableCustomTags(mappedTags);
    setCustomTagCategory(undefined);

    if (investorId) {
      setCustomTags({ investorId, customTags: mappedTags });
    }
  };

  return (
    <RecordRow label="Custom tag(s)">
      <StyledBox>
        <CustomTagSection
          disabled={!canEdit}
          onChange={handleCustomTagsChange}
          selectedCategory={customTagCategory}
          onCategorySelect={setCustomTagCategory}
          categoryOptions={tagCategoryOptions}
          tagOptions={tagOptions}
          tags={editableCustomTags}
        />
      </StyledBox>
    </RecordRow>
  );
};

export default InvestorDetailsCustomTags;
